import React, { useEffect, useState } from "react";
import Breadcrumb from "../../components/Breadcrumbs/Breadcrumb";
import SectionHeader from "../../components/SectionHeader/SectionHeader";
import FadeAlert, { FadeAlertType } from "../../components/FadeAlert/FadeAlert";
import DatePicker from "../../components/Forms/DatePicker/DatePicker";
import SelectGroup from "../../components/Forms/SelectGroup/SelectGroup";
import InsuranceService from "../../services/InsuranceService/InsuranceService";
import PolicyService from "../../services/PolicyService/PolicyService";
import { useAuth } from "../../provider/AuthProvider";
import { useLocation } from "react-router-dom";
import KFNOperationsDataGrid from "../../components/DataTables/KFNOperationsDataGrid";

const labelClass =
  "mb-1 ml-1 block text-xs font-medium text-black dark:text-white";

const KFNOperationsReport: React.FC = () => {
  const { jwt } = useAuth();
  const location = useLocation();

  const [formState, setFormState] = useState({
    fromDate: "",
    toDate: "",
    company: "",
    policyType: "",
    ext_pol: "0" as "0" | "1",
    nopay_pol: "0" as "0" | "1",
    sum_pol_0: "0" as "0" | "1",
  });

  const [companies, setCompanies] = useState<{ [key: string]: string }>({});
  const [policyTypes, setPolicyTypes] = useState<{ [key: string]: string }>({});

  const [rows, setRows] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [alert, setAlert] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<FadeAlertType>();

  // reset on route change (BankReport pattern)
  useEffect(() => {
    setFormState({
      fromDate: "",
      toDate: "",
      company: "",
      policyType: "",
      ext_pol: "0",
      nopay_pol: "0",
      sum_pol_0: "0",
    });
    setRows([]);
    setAlert(null);
    setAlertType(undefined);
  }, [location.pathname]);

  // load dropdowns
  useEffect(() => {
    const fetch = async () => {
      try {
        const [c, p] = await Promise.all([
          InsuranceService.getBaseInsuranceCompanies(jwt),
          InsuranceService.getBasePolicies(jwt),
        ]);

        setCompanies(c);
        setPolicyTypes(p);
      } catch {
        setAlert("Грешка при зареждане на справочни данни");
        setAlertType(FadeAlertType.error);
      }
    };

    fetch();
  }, [jwt]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormState((p) => ({ ...p, [name]: value }));
  };

  // Check if required fields are filled
  const isFormValid = () => {
    return (
      formState.fromDate &&
      formState.toDate &&
      formState.company &&
      formState.policyType
    );
  };

  // Get validation message for disabled button
  const getDisabledMessage = () => {
    if (!formState.fromDate || !formState.toDate) {
      return "Моля, въведете начална и крайна дата, ЗК и Вид";
    }
    if (!formState.company) {
      return "Моля, изберете ЗК";
    }
    if (!formState.policyType) {
      return "Моля, изберете Вид Полица";
    }
    return "";
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isFormValid()) {
      setAlert(getDisabledMessage());
      setAlertType(FadeAlertType.error);
      return;
    }

    try {
      setIsLoading(true);

      const result = await PolicyService.fetchKFNOperationsReport(
        formState,
        jwt
      );

      setRows(Object.values(result || {}));
    } catch {
      setAlert("Грешка при извличане на справката");
      setAlertType(FadeAlertType.error);
    } finally {
      setIsLoading(false);
    }
  };

  const isButtonDisabled = !isFormValid();
  const disabledMessage = getDisabledMessage();

  return (
    <>
      <Breadcrumb pageName="КФН Оперативни справки" />

      <div className="border-stroke shadow-default dark:border-strokedark dark:bg-boxdark rounded-sm border bg-white">
        <FadeAlert
          alert={alert}
          type={alertType}
          onClose={() => setAlert(null)}
        />

        <SectionHeader title="Оперативни справки за КФН" />

        {/* FORM */}
        <form onSubmit={handleSearch} className="space-y-4 p-3">
          {/* DATE RANGE */}
          <div className="grid grid-cols-2 gap-3">
            <DatePicker
              label="От дата"
              name="fromDate"
              value={formState.fromDate}
              onChange={handleInputChange}
            />

            <DatePicker
              label="До дата"
              name="toDate"
              value={formState.toDate}
              onChange={handleInputChange}
            />
          </div>

          {/* FILTERS */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>ЗК</label>
              <SelectGroup
                value={formState.company}
                options={companies}
                name="company"
                onChange={(value) =>
                  setFormState((p) => ({ ...p, company: value }))
                }
              />
            </div>

            <div>
              <label className={labelClass}>Вид Полица</label>
              <SelectGroup
                value={formState.policyType}
                options={policyTypes}
                name="policyType"
                onChange={(value) =>
                  setFormState((p) => ({ ...p, policyType: value }))
                }
              />
            </div>
          </div>

          {/* CHECKBOXES */}
          <div className="grid grid-cols-3 gap-3">
            <label className="flex items-center text-xs text-black dark:text-white">
              <input
                type="checkbox"
                checked={formState.ext_pol === "1"}
                onChange={(e) =>
                  setFormState((p) => ({
                    ...p,
                    ext_pol: e.target.checked ? "1" : "0",
                  }))
                }
                className="cursor-pointer"
              />
              <span className="ml-2">Без чужди полици</span>
            </label>

            <label className="flex items-center text-xs text-black dark:text-white">
              <input
                type="checkbox"
                checked={formState.nopay_pol === "1"}
                onChange={(e) =>
                  setFormState((p) => ({
                    ...p,
                    nopay_pol: e.target.checked ? "1" : "0",
                  }))
                }
                className="cursor-pointer"
              />
              <span className="ml-2">Без плащане по 1 вноска</span>
            </label>

            <label className="flex items-center text-xs text-black dark:text-white">
              <input
                type="checkbox"
                checked={formState.sum_pol_0 === "1"}
                onChange={(e) =>
                  setFormState((p) => ({
                    ...p,
                    sum_pol_0: e.target.checked ? "1" : "0",
                  }))
                }
                className="cursor-pointer"
              />
              <span className="ml-2">Без премия</span>
            </label>
          </div>

          <div className="mt-4 flex justify-center">
            <button
              type="submit"
              disabled={isButtonDisabled}
              title={disabledMessage || ""}
              className={`rounded px-4 py-1.5 text-xs text-white transition-all ${
                isButtonDisabled
                  ? "bg-accent cursor-not-allowed opacity-50"
                  : "bg-primary hover:opacity-90"
              }`}
            >
              Покажи
            </button>
          </div>
        </form>

        <div className="p-3">
          <KFNOperationsDataGrid data={rows} isLoading={isLoading} />
        </div>
      </div>
    </>
  );
};

export default KFNOperationsReport;
